/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {zodResolver} from '@hookform/resolvers/zod'
import PropTypes from 'prop-types'
import React, {useState} from 'react'
import {useForm} from 'react-hook-form'

import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Flex,
    Heading,
    Input,
    InputGroup,
    InputRightElement,
    Stack,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useNewsletterSubscribeMutation} from '../../clients/ssr/hooks/use-newsletter-subscribe'
import {localStorageKeys, styleConfigChakraKeys} from '../../constants'
import {newsletterSchema} from '../../validations'
import {useIntl} from 'react-intl'

const Subscribe = ({title, inputPlaceholder, ...otherProps}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig(styleConfigChakraKeys.footer)

    const form = useForm({resolver: zodResolver(newsletterSchema)})
    const [formSubmitSuccess, setFormSubmitSuccess] = useState(false)

    const newsletterMutation = useNewsletterSubscribeMutation()

    const sendNewsletterSubscriptionRequest = async (payload) => {
        newsletterMutation.mutate(payload, {
            onSuccess: () => {
                localStorage.setItem(
                    localStorageKeys.newsLetterSubscription,
                    'true'
                )
                setFormSubmitSuccess(true)
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    event: 'newsletter',
                    newsletterEmail: payload.email
                })
            },
            onError: (error) => {
                form.setError('global', {
                    type: 'manual',
                    message: error?.message
                })
            }
        })
    }

    return (
        <Box {...styles.subscribe} {...otherProps}>
            <Heading {...styles.subscribeHeading}>{title}</Heading>

            <Flex
                justifyContent="center"
                alignItems="center"
                onSubmit={form.handleSubmit(sendNewsletterSubscriptionRequest)}
                as="form"
            >
                {formSubmitSuccess ? (
                    <Heading
                        {...styles.subscribeSuccessMessage}
                        fontSize="24px"
                        fontWeight={600}
                    >
                        {formatMessage({
                            id: 'rennai.newsletter_subscription',
                            defaultMessage: 'Thank you for subscribing!'
                        })}
                    </Heading>
                ) : (
                    <Stack>
                        {form.formState.errors?.global && (
                            <Alert status="error">
                                <AlertIcon color="red.500" boxSize={4} />
                                <Text fontSize="sm" ml={3}>
                                    {form.formState.errors.global.message}
                                </Text>
                            </Alert>
                        )}
                        <InputGroup {...styles.subscribeInputBox}>
                            <InputRightElement
                                {...styles.subscribeButtonIcon}
                                type="submit"
                                as={Button}
                                isLoading={newsletterMutation.isLoading}
                                variant="unstyled"
                                height="44px"
                                width="44px"
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1.16663 7H12.8333M12.8333 7L6.99996 1.16667M12.8333 7L6.99996 12.8333"
                                        stroke="#8F9292"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </InputRightElement>
                            <Input
                                type="email"
                                {...form.register('email')}
                                placeholder={inputPlaceholder}
                                {...styles.subscribeField}
                            />
                        </InputGroup>
                    </Stack>
                )}
            </Flex>
        </Box>
    )
}

Subscribe.propTypes = {
    title: PropTypes.string,
    inputPlaceholder: PropTypes.string
}

export default Subscribe
